/*
 * *****************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   Thu, Aug 12th 2021, 8:39:36 am
 *
 * *****************************************************
 */

import { useContext, useMemo } from 'react';

import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';

import { GoogleReCaptchaV3ProviderContext } from 'features/common/views/components/GoogleReCaptchaV3Provider';
import { selectExpiredTime, selectJwtToken } from 'states/slices/auth/selectors';
import type { RootState, AppDispatch } from 'states/types';

import { isJWTExpired } from './jwt';

/**
 * A resolved-type useDispatch hook, use this instead of the original one (which is imported directly from 'react-redux' package)
 * @example
 * function Component() {
 *  const dispatch = useAppDispatch();
 *  // ...
 * }
 */
export const useAppDispatch = () => useDispatch<AppDispatch>();

/**
 * A resolved-type useSelector hook, use this instead of the original one (which is imported directly from 'react-redux' package)
 * @example
 * function Component() {
 *  const someState = useAppSelectr(state: RootState => state.auth.someState);
 *  // ...
 * }
 */
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

/**
 * Hook for access app authentication data (token, user data, etc.)
 * @example
 * function Component() {
 *  const { jwtToken } = useAuth();
 *  // ...
 * }
 */
export const useAuth = () => {
  const jwtToken = useAppSelector(selectJwtToken);
  const expiredTime = useAppSelector(selectExpiredTime);

  const isExpired = isJWTExpired(expiredTime);

  return useMemo(() => ({ jwtToken, isExpired }), [jwtToken, isExpired]);
};

/**
 * Hook for consuming Google ReCaptcha V3
 */
export const useGoogleReCaptchaV3 = () => {
  return useContext(GoogleReCaptchaV3ProviderContext);
};
